// FILE: src/services/dataToolsService.js
//
// Service layer for the Admin → Data Tools panel.  Three capabilities:
//
//   1. clearTestSessions()       — deletes all exam_sessions rows where
//                                  source='test'.  Used to reset the
//                                  dashboard counts after a burst of CI
//                                  Playwright runs pollutes the table.
//
//   2. clearAllUserSessions()    — nuclear reset of source='user' rows.
//                                  The UI gates this behind a type-DELETE
//                                  confirmation dialog.
//
//   3. generateTestQuestions()   — pure client-side helper that returns N
//                                  synthetic questions in the same shape
//                                  handleImport() expects.  Used to drop a
//                                  quick batch of throwaway questions into
//                                  staging for feature validation.
//
// The two delete operations call SECURITY DEFINER RPCs (migration 010)
// rather than issuing direct DELETEs, because the exam_sessions table
// intentionally has no DELETE RLS policy for the anon role.

import { supabase } from './supabaseClient';
import { EXAM_CATEGORIES } from '../config/examCategories';

// ── Session delete RPCs ──────────────────────────────────────────────────

/**
 * Deletes every exam_sessions row tagged source='test'.
 * Returns the count of rows deleted.
 *
 * @returns {Promise<number>}
 * @throws {Error} on any Supabase failure
 */
export async function clearTestSessions() {
  const { data, error } = await supabase.rpc('admin_clear_test_sessions');
  if (error) throw new Error(`Clear test sessions failed: ${error.message}`);
  return typeof data === 'number' ? data : 0;
}

/**
 * Deletes every exam_sessions row tagged source='user'.
 * This is a nuclear operation — the UI gates it behind a type-DELETE
 * confirmation prompt.
 *
 * @returns {Promise<number>}
 * @throws {Error} on any Supabase failure
 */
export async function clearAllUserSessions() {
  const { data, error } = await supabase.rpc('admin_clear_user_sessions_all');
  if (error) throw new Error(`Clear user sessions failed: ${error.message}`);
  return typeof data === 'number' ? data : 0;
}

// ── Synthetic question generator ─────────────────────────────────────────
//
// Categories are sourced from src/config/examCategories.js so adding a new
// certification there automatically gives "Load Test Data" the right
// taxonomy — no per-exam constants needed in this file.

const PMP_PERFORMANCE_DOMAINS = ['People', 'Process', 'Business'];
const PMP_DELIVERY_CONTEXTS   = ['Predictive', 'Agile', 'Hybrid'];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Stable-per-call random suffix so a batch of 5 has unique ids even
// when generated within the same millisecond.
function shortId() {
  return Math.random().toString(36).slice(2, 7);
}

/**
 * Produces N synthetic questions for quick end-to-end validation.
 *
 * The returned questions are deliberately obvious fakes:
 *   - id / referenceId embed a "TEST-" prefix and timestamp
 *   - question text names itself as synthetic
 *   - one multi-select is mixed in so both UI paths get exercised
 *   - status='new' + reviewStatus='unreviewed' so they appear in the
 *     review queue and can be cleaned up easily
 *
 * @param {'pmp'|'cpmai'|'pmi-acp'|string} examId
 * @param {number}                         [count=5]
 * @returns {object[]} questions in the shape handleImport() expects
 */
export function generateTestQuestions(examId, count = 5) {
  const stamp = Date.now();
  const batch = `test-data-${stamp}`;
  const isPmp = examId === 'pmp';
  // Canonical taxonomy comes from EXAM_CATEGORIES (config/examCategories.js).
  // Falls back to a single 'General' bucket for any future exam that hasn't
  // been registered there yet so generation still produces usable rows.
  const categories = (EXAM_CATEGORIES[examId] && EXAM_CATEGORIES[examId].length > 0)
    ? EXAM_CATEGORIES[examId]
    : ['General'];

  const questions = [];
  for (let i = 0; i < count; i++) {
    const suffix = shortId();
    const n = i + 1;
    // Every 3rd question is multi-select to exercise both checkbox and
    // radio paths.
    const isMulti = i % 3 === 2;

    const choices = [
      { id: 'A', text: `Synthetic answer A for test question ${n}` },
      { id: 'B', text: `Synthetic answer B for test question ${n}` },
      { id: 'C', text: `Synthetic answer C for test question ${n}` },
      { id: 'D', text: `Synthetic answer D for test question ${n}` },
    ];
    const correctAnswers = isMulti ? ['A', 'C'] : ['A'];

    questions.push({
      id:              `${examId}-test-${stamp}-${suffix}`,
      referenceId:     `TEST-${stamp}-${String(n).padStart(2, '0')}`,
      question:        `[SYNTHETIC #${n}] This is a placeholder test question generated from the Admin Data Tools panel. Select the "correct" answer to validate the question rendering and persistence pipeline.`,
      choices,
      correctAnswers,
      explanation:     'Synthetic explanation — this question was generated for feature validation and should be retired after use.',
      category:        pick(categories),
      confidence:      'low',
      multiSelect:     isMulti,
      status:          'new',
      reviewStatus:    'unreviewed',
      sourceBatchId:   batch,
      sourceGenerator: 'admin-data-tools/v1',
      // PMP-only taxonomy — left null for CPMAI.
      performanceDomain: isPmp ? pick(PMP_PERFORMANCE_DOMAINS) : null,
      deliveryContext:   isPmp ? pick(PMP_DELIVERY_CONTEXTS)   : null,
    });
  }
  return questions;
}
